<script>
import date from 'locutus/php/datetime/date'
import {
  fetchAssets,
  fetchAsset,
  fetchWithdrawFees,
  fetchWithdrawFee,
  createWithdrawFee,
  updateWithdrawFee,
  deleteWithdrawFee,
} from '@apis/admin/asset'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import find from 'lodash/find'

export default {
  props: {
    asset_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      withdraw_fees: [],
      assets: [],
      asset_info: null,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    asset_seq() {
      this.init()
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([
        this.loadWithdrawFees(),
        this.loadAssets(),
        this.loadAssetInfo(),
      ]).catch((e) => this.$toasted.error(e.message))
    },
    async loadWithdrawFees() {
      const { data } = await fetchWithdrawFees({
        asset_seq: this.asset_seq,
      })

      this.withdraw_fees = data
    },
    async loadAssets() {
      const { data } = await fetchAssets({ no_paging: 1 })

      this.assets = data
    },
    async loadAssetInfo() {
      const { asset_info } = await fetchAsset(this.asset_seq)

      this.asset_info = asset_info
    },
    handleAddWithdrawFee() {
      this.withdraw_fees.push({
        asset_seq: this.asset_seq,
        fee_asset_seq: null,
        is_equal_account_required: 'Y',
        fee_type: 'PREPAY',
        fee_amount_type: 'FIXED',
        fee_amount: '0',
        list_order: 0,
      })
    },
    async handleDeleteWithdrawFee(idx) {
      this.loading = true
      try {
        if (this.withdraw_fees[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteWithdrawFee(this.withdraw_fees[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.withdraw_fees.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitWithdrawFee(idx) {
      this.loading = true
      const params = this.withdraw_fees[idx]
      ;(params.seq ? updateWithdrawFee : createWithdrawFee)({
        ...params,
        fee_asset_seq:
          params.is_equal_account_required === 'Y'
            ? this.asset_seq
            : params.fee_asset_seq,
        fee_type:
          params.is_equal_account_required !== 'Y' ? 'PREPAY' : params.fee_type,
      })
        .then(({ seq }) => fetchWithdrawFee(seq))
        .then(({ fee_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.withdraw_fees, idx, { ...fee_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
    find,
  },
}
</script>

<template>
  <div>
    <table
      v-if="asset_seq"
      cellspacing="0"
      cellpadding="0"
      :class="$style.colTable"
    >
      <thead>
        <tr>
          <th scope="col">{{ $t('asset_withdraw_fee.order') }}</th>
          <th scope="col">{{ $t('asset_withdraw_fee.commission') }}</th>
          <th scope="col">
            <div :class="$style.tRight">
              <a
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleAddWithdrawFee"
                ><span>{{ $t('admin.add') }}</span></a
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(withdraw_fee, idx) in withdraw_fees"
          :key="withdraw_fee.seq ? `${withdraw_fee.seq}` : `create-${idx}`"
        >
          <td :class="[$style.tCenter, $style.nowrap]">
            <input
              v-model="withdraw_fee.list_order"
              type="text"
              :class="[$style.inputTypeText, $style.w40, $style.tRight]"
            />
          </td>
          <td :class="$style.wide">
            <select v-model="withdraw_fee.is_equal_account_required">
              <option
                v-for="[value, title] in [
                  [
                    'Y',
                    $t('asset_withdraw_fee.withdrawal_commission_same'),
                  ],
                  ['N', $t('asset_withdraw_fee.fee_payment_account_can')],
                ]"
                :key="value"
                :value="value"
              >
                {{ title }}
              </option>
            </select>
            해야 하고
            <template v-if="withdraw_fee.is_equal_account_required === 'Y'">
              {{ find(assets, { seq: asset_seq }).title }}
            </template>
            <ElSelect v-else v-model="withdraw_fee.fee_asset_seq">
              <ElOption
                v-for="asset in assets"
                :key="asset.seq"
                :label="asset.title"
                :value="asset.seq"
              >
              </ElOption>
            </ElSelect>

            자산으로 납부 시
            <template v-if="withdraw_fee.is_equal_account_required !== 'Y'">
              {{ fromPairs(constants.asset.withdraw_fee.fee_type).PREPAY }}
            </template>
            <select v-else v-model="withdraw_fee.fee_type">
              <option
                v-for="val in constants.asset.withdraw_fee.fee_type"
                :key="val[0]"
                :value="val[0]"
                >{{ val[1] }}</option
              >
            </select>
            <template>
              하며 부과 금액을
              <select v-model="withdraw_fee.fee_amount_type">
                <option
                  v-for="val in constants.asset.withdraw_fee.fee_amount_type"
                  :key="val[0]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              {{ ' ' }}
              <input
                v-model="withdraw_fee.fee_amount"
                type="text"
                :class="[$style.inputTypeText, $style.w100, $style.tRight]"
              />
              {{
                withdraw_fee.fee_amount_type === 'RATIO'
                  ? '%'
                  : (
                      find(assets, {
                        seq:
                          withdraw_fee.is_equal_account_required === 'Y'
                            ? asset_seq
                            : withdraw_fee.fee_asset_seq,
                      }) || {}
                    ).code
              }}
              로 지정함
            </template>
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              v-if="withdraw_fee.seq"
              href="#"
              :class="[$style.button]"
              @click="handleSubmitWithdrawFee(idx)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
            <a
              v-else
              href="#"
              :class="[$style.black, $style.button]"
              @click="handleSubmitWithdrawFee(idx)"
              ><span>{{ $t('admin.enroll') }}</span></a
            >
            {{ ' ' }}
            <a
              href="#"
              :class="[$style.red, $style.button]"
              @click="handleDeleteWithdrawFee(idx)"
              ><span>{{ $t('admin.delete') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="!withdraw_fees.length">
          <td :class="$style.tCenter" colspan="3">{{
            $t('asset_withdraw_fee.msg_asset_withdrawal_fee_no')
          }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
.adminRightExtra p {
  margin: 10px 0;
}
</style>
